// src/pages/ConversationsList.js
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Tag } from "lucide-react";
import { listenToInbox, getLastMessagePreview, getChatMeta } from "../utils/chatUtils";
import useAuth from "../hooks/useAuth";
import { userRepository } from "../repositories/userRepository";
import { listenNotificationsForUser } from "../services/notificationsService";
import { isChatUnreadForUser } from "../utils/unread";
import { errorDev } from "../utils/logger";

const userProfiles = new Map(); // simple in-memory cache across component instances

function stringifyPreviewText(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  if (v && typeof v === "object") {
    if (typeof v.toDate === "function") {
      try {
        return v.toDate().toLocaleString();
      } catch (err) { errorDev("Operation failed:", err);}
    }
    if ("seconds" in v) {
      try {
        return new Date(v.seconds * 1000).toLocaleString();
      } catch (err) { errorDev("Operation failed:", err);}
    }
    try {
      return JSON.stringify(v);
    } catch (err) {
      errorDev("Operation failed:", err);
      return String(v);
    }
  }
  return String(v);
}

function prettyAmount(candidate) {
  if (candidate == null) return null;
  const n = typeof candidate === "number" ? candidate : Number(candidate);
  if (Number.isFinite(n)) {
    try {
      return `${n.toLocaleString()} RWF`;
    } catch (err) {
      errorDev("Operation failed:", err);
      return `${n} RWF`;
    }
  }
  return String(candidate);
}

function truncate(s, len = 80) {
  if (!s) return "";
  return s.length > len ? s.slice(0, len - 1) + "…" : s;
}

export default function ConversationsList({ onSelectChat }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);
  const navigate = useNavigate();

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!user) {
      setRows([]);
      setLoading(false);
      return;
    }

    let unsubInbox = null;
    let unsubNotifs = null;

    try {
       // inbox listener (primary)
      unsubInbox = listenToInbox(user.uid, async (chats) => {
        if (!mountedRef.current) return;

        const chatList = chats || [];

        // Batch-fetch uncached participant profiles to avoid N+1 getById calls
        const uncachedIds = Array.from(new Set(
          chatList
            .map((c) => (c.participants || []).find((id) => id !== user.uid))
            .filter((id) => id && !userProfiles.has(id))
        ));

        if (uncachedIds.length > 0) {
          const profiles = await userRepository.getByIds(uncachedIds);
          const profilesById = new Map(profiles.map((p) => [p.id, p]));
          uncachedIds.forEach((id) => {
            userProfiles.set(id, profilesById.has(id) ? { ...profilesById.get(id) } : { id });
          });
        }

        const enriched = await Promise.all(chatList.map(async (c) => {
          const otherId = (c.participants || []).find((id) => id !== user.uid);
          const other = otherId ? (userProfiles.get(otherId) || { id: otherId }) : null;

          let preview = null;
          try {
            preview = await getLastMessagePreview(c.id);
          } catch (e) {
            preview = null;
          }

          return { ...c, other, preview };
        }));
        if (mountedRef.current) {
          setRows((prev) => {
            // keep ordering from server but try to merge intelligently: prefer server list
            return enriched;
          });
          setLoading(false);
        }
      });
    } catch (err) {
      errorDev("ConversationsList: failed to attach inbox listener", err);
      if (mountedRef.current) {
        setRows([]);
        setLoading(false);
      }
    }

    // supplemental listener: if notifications mention a chatId, ensure chat meta is fetched & surfaced
    try {
      unsubNotifs = listenNotificationsForUser(user.uid, async (notifs) => {
        if (!mountedRef.current) return;
        try {
          const newMetas = [];

          for (const n of (notifs || [])) {
            const chatId = n?.payload?.chatId || n?.chatId || n?.payload?.data?.chatId || null;
            if (!chatId) continue;
            // if already present in rows, skip
            const exists = rows.some(r => String(r.id) === String(chatId));
            if (exists) continue;
            // fetch chat meta and add it to the top of the inbox
            try {
              const meta = await getChatMeta(chatId);
              if (meta) {
                const otherId = (meta.participants || []).find((id) => id !== user.uid);
                newMetas.push({ meta, otherId });
              }
            } catch (e) {
              // ignore per-chat failure
            }
          }

          if (newMetas.length === 0) return;

          // Batch-fetch uncached participant profiles
          const uncachedIds = Array.from(new Set(
            newMetas
              .map((c) => c.otherId)
              .filter((id) => id && !userProfiles.has(id))
          ));

          if (uncachedIds.length > 0) {
            const profiles = await userRepository.getByIds(uncachedIds);
            const profilesById = new Map(profiles.map((p) => [p.id, p]));
            uncachedIds.forEach((id) => {
              userProfiles.set(id, profilesById.has(id) ? { ...profilesById.get(id) } : { id });
            });
          }

          for (const { meta, otherId } of newMetas) {
            const other = otherId ? (userProfiles.get(otherId) || { id: otherId }) : null;
            const preview = await getLastMessagePreview(meta.id).catch(() => null);
            if (mountedRef.current) {
              setRows((prev) => {
                // avoid duplicates
                if (prev.some(p => p.id === meta.id)) return prev;
                return [{ ...meta, other, preview }, ...prev];
              });
            }
          }
        } catch (e) {
          // ignore
        }
      });
    } catch (e) {
      // not fatal if notification service unavailable
    }

    return () => {
      try { unsubInbox && unsubInbox(); } catch (_) {}
      try { unsubNotifs && unsubNotifs(); } catch (_) {}
    };
  }, [user, /* NOTE: do not include rows here or we'll re-trigger loops */]);

  if (!user) return null;

  if (loading) {
    return <div className="p-4 text-muted-foreground">Loading inbox…</div>;
  }

  if (rows.length === 0) {
    return <div className="p-4 text-muted-foreground">No conversations yet.</div>;
  }

  function openChat(c) {
    if (!c || !c.id) return;
    if (onSelectChat) {
      try { onSelectChat(c.id, c.other?.id, c.other?.displayName || c.other?.name); } catch (e) { errorDev("onSelectChat threw", e); }
      return;
    }
    const encoded = encodeURIComponent(String(c.id));
      try { navigate(`/messages?chatId=${encoded}`); } catch (e) {
        try { navigate(`/messages/${String(c.id)}`); } catch (e2) { errorDev("navigate failed", e2); }
    }
  }

  return (
    <div className="divide-y border border-border rounded-lg overflow-hidden bg-card shadow-sm">
      {rows.map((c) => {
        const hasUnread = isChatUnreadForUser(c, user.uid);

        const title = c.other?.displayName || c.other?.name || (c.other?.id ? c.other.id : "User");

        // Determine preview: prefer last message text (preview), else lastMessage, else offer fallback
        let line = "Start chatting…";
        if (c.preview && (c.preview.text || c.preview.lastMessage)) {
          line = stringifyPreviewText(c.preview.text || c.preview.lastMessage);
        } else if (c.lastMessage) {
          line = stringifyPreviewText(c.lastMessage);
        } else {
          const offerCandidate = c.lastOfferAmount ?? c.preview?.offerAmount ?? c.preview?.extra?.offerAmount ?? c.lastOfferAmount;
          if (offerCandidate != null) {
            line = `Offer: ${prettyAmount(offerCandidate)}`;
          } else {
            line = "Start chatting…";
          }
        }

        line = truncate(line, 90);

        const avatarSrc = c.other?.photo || c.other?.photoURL || c.other?.poster?.photo || null;
        const initials = (c.other?.displayName || c.other?.name || c.other?.id || "U").slice(0,2).toUpperCase();

        return (
          <button
            key={c.id}
            className="w-full text-left px-4 py-3 hover:bg-muted flex items-start justify-between gap-3 transition-colors"
            onClick={() => openChat(c)}
            aria-label={`Open conversation with ${title}`}
            title={line}
          >
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-xs font-semibold text-foreground overflow-hidden flex-shrink-0">
                {avatarSrc ? (
                  <img src={avatarSrc} alt={title} className="w-full h-full object-cover" onError={(e)=>{ e.currentTarget.src=''; }} />
                ) : (
                  <span>{initials}</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-foreground text-sm">{title}</div>
                <div className="text-xs text-muted-foreground line-clamp-1">{line}</div>
              </div>
            </div>

            {hasUnread && (
              <span className="ml-3 inline-flex items-center justify-center min-w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs px-2 font-semibold flex-shrink-0">
                New
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
